import React, { useState, useEffect, useCallback, Fragment } from "react";
import { useSelector } from "react-redux";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Dialog, Transition } from "@headlessui/react";
import ApiClient from "../../methods/api/apiClient";
import loader from "../../methods/loader";
import { toast } from "react-toastify";
import swal from "sweetalert";
import Header from "../Components/Header";
import Footer from "../Components/Footer";
import methodModel from "../../methods/methods";
import { CiLocationOn } from "react-icons/ci";
import { GoHash, GoPackage } from "react-icons/go";
import { LuClipboardList, LuListTodo } from "react-icons/lu";
import { LiaClipboardListSolid } from "react-icons/lia";
import Select from "react-select";

// ─── normalizeEstimate (same as list page) ────────────────────────────────────
const normalizeEstimate = (est) => {
  const isQB = est.type === "quickbookEstimate";
  const raw = est.raw || {};

  let rawStatus =
    est.status || raw.TxnStatus || est.mainRequest?.status || "PENDING";
  let status = String(rawStatus).toUpperCase();
  if (status === "ESTIMATED" || status === "ESTIMATE") {
    status = "PENDING";
  } else if (status === "NEGOTIATION") {
    status = "NEGOTIATE";
  }
  if (status === "SYNCED") status = "ACCEPTED";
  if (status === "CONVERTED") status = "CLOSED";

  const id = est._id;
  const qbId = est.qbId || raw.Id || null;
  const docNumber =
    est.docNumber ||
    est.doc_number ||
    est.estimate_no ||
    raw.DocNumber ||
    `EST-${id?.slice(-6)}`;

  const customerName =
    est.customerName ||
    est.customer_name ||
    raw.CustomerRef?.name ||
    est.displayName ||
    "—";
  const email = est.email || raw.BillEmail?.Address || est.displayEmail || "—";

  const txnDate =
    est.txnDate || est.txn_date || raw.TxnDate || est.createdAt || null;
  const expirationDate = est.expiration_date || raw.ExpirationDate || null;

  const currencyCode = raw.CurrencyRef?.value || "USD";

  const billAddr =
    raw.BillAddr ||
    (est.billingAddress
      ? {
          Line1: est.billingAddress.bill_line1,
          Line2: est.billingAddress.bill_line2,
          City: est.billingAddress.bill_city,
          Country: est.billingAddress.bill_country,
          PostalCode: est.billingAddress.bill_postal_code,
        }
      : est.originalQuoteData?.BillAddr
        ? {
            Line1: est.originalQuoteData.BillAddr.Line1,
            Line2: est.originalQuoteData.BillAddr.Line2,
            City: est.originalQuoteData.BillAddr.City,
            Country: est.originalQuoteData.BillAddr.Country,
            PostalCode: est.originalQuoteData.BillAddr.PostalCode,
          }
        : null);

  const shipAddr =
    raw.ShipAddr ||
    (est.shippingAddress
      ? {
          Line1: est.shippingAddress.ship_line1,
          Line2: est.shippingAddress.ship_line2,
          City: est.shippingAddress.ship_city,
          Country: est.shippingAddress.ship_country,
          PostalCode: est.shippingAddress.ship_postal_code,
        }
      : est.originalQuoteData?.ShipAddr
        ? {
            Line1: est.originalQuoteData.ShipAddr.Line1,
            Line2: est.originalQuoteData.ShipAddr.Line2,
            City: est.originalQuoteData.ShipAddr.City,
            Country: est.originalQuoteData.ShipAddr.Country,
            PostalCode: est.originalQuoteData.ShipAddr.PostalCode,
          }
        : null);

  let rawLines = est?.customer_line_items || raw.Line;
  if (!rawLines || rawLines.length === 0) {
    rawLines =
      est.line_items && est.line_items.length > 0
        ? est.line_items
        : est.product_line_items;
  }
  if (!rawLines || rawLines.length === 0) {
    rawLines = est.originalQuoteData?.Line || [];
  }
  const lineItems = rawLines
    .filter(
      (l) =>
        l.DetailType === "SalesItemLineDetail" ||
        l.item_name ||
        l.name ||
        l.productName ||
        l.Description ||
        l.description,
    )
    .map((line, index) => {
      let itemName = "—",
        quantity = 1,
        unitPrice = 0,
        amount = 0,
        description = "";
      let productId = "";

      if (
        line.DetailType === "SalesItemLineDetail" &&
        line.SalesItemLineDetail
      ) {
        itemName =
          line.SalesItemLineDetail.ItemRef?.name || line.Description || "—";
        quantity = Number(line.SalesItemLineDetail.Qty || line.qty || 1);
        unitPrice = Number(
          line.SalesItemLineDetail.UnitPrice || line.rate || 0,
        );
        amount = Number(line.Amount || line.amount || quantity * unitPrice);
        description = line.Description || "";
        productId =
          line.SalesItemLineDetail.ItemRef?.value || line.db_product_id || "";
      } else if (line.item_name || line.name || line.productName) {
        itemName = line.item_name || line.name || line.productName;
        quantity = Number(line.quantity || line.qty || 1);
        unitPrice = Number(line.price || line.unitPrice || line.rate || 0);
        amount = Number(line.amount || line.total || quantity * unitPrice);
        description = line.description || line.Description || "";
        productId = line.productId || line.item_name || "";
      }

      return {
        id: line.Id || `line-${index}`,
        productId,
        itemName,
        description,
        quantity,
        unitPrice,
        amount,
      };
    });

  const calculatedSubtotal = lineItems.reduce(
    (acc, item) => acc + (item.amount || 0),
    0,
  );

  const tax = Number(
    est.totalTax ?? est.total_tax ?? raw.TxnTaxDetail?.TotalTax ?? 0,
  );

  let discountType = est.discount_type || null;
  let discountValue = est.discount_value || 0;
  let calculatedDiscount = 0;

  if (isQB) {
    const discountLine =
      rawLines.find((l) => l.DetailType === "DiscountLineDetail") || null;
    if (discountLine) {
      discountValue = Number(discountLine.Amount || 0);
      if (discountLine.DiscountLineDetail?.PercentBased) {
        discountType = "percent";
        discountValue = Number(
          discountLine.DiscountLineDetail.DiscountPercent || 0,
        );
      }
    }
  }
  if (discountType === "percent") {
    calculatedDiscount = (calculatedSubtotal * discountValue) / 100;
  } else {
    calculatedDiscount = discountValue;
  }

  const total = Number(
    est.totalAmount ??
      est.total_amount ??
      raw.TotalAmt ??
      calculatedSubtotal + tax - calculatedDiscount,
  );

  const customFields = raw.CustomField || est.CustomField || [];
  let displayDocNumber = docNumber;
  const topsField = customFields?.find(
    (f) => f?.Name === "Tops Shipping Systems, Corp",
  );
  if (
    topsField &&
    topsField.StringValue &&
    topsField.StringValue !== "ORD - null"
  ) {
    displayDocNumber = topsField?.StringValue?.replace("ORD - ", "");
  }

  const poNumber =
    est.po_number ||
    raw.PONumber ||
    customFields.find((f) => f.DefinitionId === "3")?.StringValue ||
    "—";

  const customerMemo = est.customer_memo || raw.CustomerMemo?.value || null;
  const privateNote = est.private_note || raw.PrivateNote || null;
  const negotiateMessage =
    est.negotiateMessage ||
    est.negotiation_message ||
    raw.negotiateMessage ||
    null;

  return {
    id,
    qbId,
    type: est.type || "unknown",
    isQB,
    docNumber,
    displayDocNumber,
    customerName,
    email,
    status,
    txnDate,
    expirationDate,
    currencyCode,
    billAddr,
    shipAddr,
    lineItems,
    subtotal: calculatedSubtotal,
    tax,
    discountType,
    discountValue,
    discountAmt: calculatedDiscount,
    markupDetails: est?.markupDetails?.customer_subtotal
      ? est?.markupDetails
      : {
          ...est?.markup,
          customer_subtotal:
            parseFloat(calculatedSubtotal) + est?.markup?.amount,
        },
    total,
    poNumber,
    customerMemo,
    privateNote,
    negotiateMessage,
    requestedProducts: est.requested_products || [],
    mainRequestId: est.mainRequest?._id || raw.Id || id,
    originalData: est,
    domain: raw.domain || "QBO",
    taxDetail: raw.TxnTaxDetail || (tax ? { TotalTax: tax } : null),
    isPending: ["PENDING", "NEGOTIATE", "REJECTED"].includes(status),
  };
};

// ─── Helpers ───────────────────────────────────────────────────────────────────

const formatCurrency = (val) => {
  const num = Number(val ?? 0);
  const truncated = Math.trunc(num * 100) / 100;

  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(truncated);
};

const formatDate = (str) => {
  if (!str) return "—";
  const d = new Date(str);
  if (isNaN(d)) return "—";
  return d.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const getInitials = (name = "") => {
  const parts = name.trim().split(" ").filter(Boolean);
  if (!parts.length) return "?";
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const statusColor = (status) => {
  const s = String(status || "").toUpperCase();
  const map = {
    ACCEPTED: { text: "text-[#008236]", dot: "bg-[#0a7822a6]" },
    PENDING: { text: "text-amber-700", dot: "bg-amber-500" },
    NEGOTIATE: { text: "text-blue-700", dot: "bg-blue-500" },
    REJECTED: { text: "text-red-600", dot: "bg-red-500" },
    CLOSED: { text: "text-gray-600", dot: "bg-gray-400" },
    CONVERTED: { text: "text-[#115B9D]", dot: "bg-[#115B9D]" },
    DRAFT: { text: "text-gray-600", dot: "bg-gray-400" },
    SENT: { text: "text-blue-600", dot: "bg-blue-500" },
  };
  return map[s] || { text: "text-gray-600", dot: "bg-gray-400" };
};

const StatusBadge = ({ status }) => {
  const c = statusColor(status);
  return (
    <span
      className={`inline-flex items-center gap-1.5 text-xs sm:text-sm font-medium ${c.text}`}
    >
      <span className={`size-1.5 rounded-full ${c.dot}`} />
      {status || "—"}
    </span>
  );
};

// ─── Main Component ────────────────────────────────────────────────────────────
export default function QuickBookEstimateDetailCustomer() {
  const { id } = useParams();
  const location = useLocation();
  const [quotesToken, setQuotesToken] = useState("");
  const [quotesCompanyId, setQuotesCompanyId] = useState("");
  const searchParams = new URLSearchParams(location.search);
  const triggerDate = searchParams.get("date");
  const history = useNavigate();
  const user = useSelector((state) => state.user);

  const [selectedEstimate, setSelectedEstimate] = useState(null);
  const [pageLoading, setPageLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);

  const [isPoNumberOpen, setIsPoNumberOpen] = useState(false);
  const [poNumber, setPoNumber] = useState("");

  const [isNegotiationModalOpen, setIsNegotiationModalOpen] = useState(false);
  const [negotiationData, setNegotiationData] = useState({
    products: [],
    message: "",
    selectedProducts: [],
  });
  const [availableProducts, setAvailableProducts] = useState([]);

  const fetchQuotes = async () => {
    await ApiClient.get("quickbook/express/tkn")
      .then((res) => {
        if (res?.success) {
          setQuotesToken(res?.data?.quick_book_accesToken);
          setQuotesCompanyId(res?.data?.quick_book_companyId);
        }
      })
      .catch(console.error);
  };

  useEffect(() => {
    fetchQuotes();
  }, []);

  // ── Fetch estimate by id ──────────────────────────────────────────────────
  const fetchEstimate = useCallback(
    async (estimateId) => {
      setPageLoading(true);
      loader(true);
      try {
        const res = await ApiClient.get(
          `quickbooks-docs/estimates/list?page=1&limit=20&id=${estimateId}&email=${user?.email}`,
        ).catch(() => null);
        const list = Array.isArray(res?.data) ? res.data : [];
        const match = list.find(
          (e) => e._id === estimateId || e.qbId?.toString() === estimateId,
        );
        if (match) {
          setSelectedEstimate(normalizeEstimate(match));
        } else {
          toast.error("Quote not found");
          history(-1);
        }
      } catch (err) {
        console.error("Error fetching estimate:", err);
      } finally {
        setPageLoading(false);
        loader(false);
      }
    },
    [user, history],
  );

  useEffect(() => {
    if (id) fetchEstimate(id);
  }, [id, triggerDate, fetchEstimate]);

  // ── Fetch available products from QuickBooks ──────────────────────────────
  const fetchAvailableProducts = async () => {
    loader(true);
    try {
      const response = await ApiClient.post("quickbook/items/db", {
        quickbook_token: quotesToken,
        company: quotesCompanyId,
      });
      if (response?.success) {
        setAvailableProducts(
          response.data?.map((item) => ({
            ...item,
            Name: item?.name,
            Description: item?.description,
            Id: item?.qbId,
            UnitPrice: item?.unitPrice,
          })) || [],
        );
      }
    } catch (error) {
      console.error("Error fetching products:", error);
    } finally {
      loader(false);
    }
  };

  // ── Negotiation message generator ─────────────────────────────────────────
  const generateNegotiationMessage = (
    estimate,
    originalProducts,
    updatedProducts,
  ) => {
    let message = `I would like to negotiate estimate #${estimate.displayDocNumber}.\n\n`;
    const changes = [];

    updatedProducts.forEach((updated) => {
      const original = originalProducts.find(
        (p) => p.productId === updated.productId,
      );
      if (!original) {
        changes.push(
          `Product: ${updated.productName}\nQuantity: ${updated.quantity}\nUnit Price: ${formatCurrency(updated.unitPrice)}\nTotal: ${formatCurrency(updated.quantity * updated.unitPrice)}, \n`,
        );
        return;
      }
      const productChanges = [];
      if (original.productName !== updated.productName)
        productChanges.push(
          `Product: previous - ${original.productName} now - ${updated.productName}`,
        );
      if (original.quantity !== updated.quantity)
        productChanges.push(
          `Quantity: previous - ${original.quantity} now - ${updated.quantity}`,
        );
      if (original.unitPrice !== updated.unitPrice)
        productChanges.push(
          `Unit Price: previous - ${formatCurrency(original.unitPrice)} now - ${formatCurrency(updated.unitPrice)}`,
        );
      const origTotal = original.quantity * original.unitPrice;
      const updTotal = updated.quantity * updated.unitPrice;
      if (origTotal !== updTotal)
        productChanges.push(
          `Total: previous - ${formatCurrency(origTotal)} now - ${formatCurrency(updTotal)}`,
        );
      if ((original.description || "") !== (updated.description || ""))
        productChanges.push(
          `Description: previous - ${original.description || "-"} now - ${updated.description || "-"}`,
        );
      if (productChanges.length > 0)
        changes.push(`Updated Product:\n${productChanges.join("\n")}`);
    });

    message +=
      changes.length === 0
        ? "No changes have been made."
        : changes.join("\n\n");
    return message;
  };

  useEffect(() => {
    if (!selectedEstimate || !negotiationData) return;
    const message = generateNegotiationMessage(
      selectedEstimate,
      negotiationData.originalProducts || [],
      negotiationData.selectedProducts || [],
    );
    setNegotiationData((prev) => ({ ...prev, message }));
  }, [negotiationData?.selectedProducts]);

  // ── Open negotiation modal ────────────────────────────────────────────────
  const openNegotiationModal = async () => {
    await fetchAvailableProducts();

    const autoPopulatedProducts = selectedEstimate.lineItems.map(
      (line, index) => ({
        id: line.id || Date.now() + index,
        productId: line.productId || "",
        productName: line.itemName || "Product",
        quantity: line.quantity || 1,
        unitPrice: line.unitPrice || 0,
        description: line.description || "",
        amount: line.amount || 0,
      }),
    );

    const validProducts = autoPopulatedProducts.filter(
      (product) => product.productName !== "Product" || product.unitPrice > 0,
    );

    setNegotiationData({
      products: validProducts,
      message: "",
      selectedProducts:
        validProducts.length > 0
          ? validProducts
          : [
              {
                id: Date.now(),
                productId: "",
                productName: "",
                quantity: 1,
                unitPrice: 0,
                description: "",
                amount: 0,
              },
            ],
    });
    setIsNegotiationModalOpen(true);
  };

  const addNegotiationProduct = () => {
    setNegotiationData((prev) => ({
      ...prev,
      selectedProducts: [
        ...prev.selectedProducts,
        {
          id: Date.now(),
          productId: "",
          productName: "",
          quantity: 1,
          unitPrice: 0,
          description: "",
        },
      ],
    }));
  };

  const updateNegotiationProduct = (index, field, value) => {
    const updatedProducts = [...negotiationData.selectedProducts];

    if (field === "productId") {
      const selectedProduct = availableProducts.find(
        (p) =>
          p.Id === value ||
          p.Name?.toLowerCase() === value?.toLowerCase() ||
          p.Id?.toString() === value?.toString(),
      );
      if (selectedProduct) {
        updatedProducts[index].productId = selectedProduct.Id;
        updatedProducts[index].productName = selectedProduct.Name;
        updatedProducts[index].unitPrice = selectedProduct.UnitPrice || 0;
        updatedProducts[index].description = selectedProduct.Description || "";
      } else if (value && typeof value === "string") {
        updatedProducts[index].productId = value;
        updatedProducts[index].productName = value;
      }
    } else if (field === "quantity") {
      updatedProducts[index].quantity = value === "" ? "" : parseFloat(value);
    } else if (field === "unitPrice") {
      updatedProducts[index].unitPrice = value === "" ? "" : parseFloat(value);
    } else {
      updatedProducts[index][field] = value;
    }

    setNegotiationData((prev) => ({
      ...prev,
      selectedProducts: updatedProducts,
    }));
  };

  const removeNegotiationProduct = (index) => {
    setNegotiationData((prev) => ({
      ...prev,
      selectedProducts: prev.selectedProducts.filter((_, i) => i !== index),
    }));
  };

  // ── Send counter offer ────────────────────────────────────────────────────
  const sendCounterOffer = async () => {
    if (!selectedEstimate) return;

    const hasInvalidProduct = negotiationData.selectedProducts.some(
      (p) =>
        ((p.productId && p.productId !== "") ||
          (p.productName && p.productName !== "")) &&
        (Number(p.quantity) <= 0 || Number(p.unitPrice) <= 0),
    );

    if (hasInvalidProduct) {
      toast.error("Quantity and Unit Price must be greater than 0");
      return;
    }

    const validProducts = negotiationData.selectedProducts.filter(
      (p) =>
        (p.productId && p.productId !== "") ||
        (p.productName && p.productName !== ""),
    );

    if (validProducts.length === 0) {
      toast.error("Please select at least one product for negotiation");
      return;
    }

    setActionLoading(selectedEstimate.id);
    loader(true);

    try {
      const totalAmount = validProducts.reduce(
        (sum, p) => sum + Number(p.quantity) * Number(p.unitPrice),
        0,
      );
      const taxPercent = Number(selectedEstimate?.originalData?.tax_percent);
      const totalTaxAmt = (totalAmount * taxPercent) / 100;
      console.log(selectedEstimate, "selectedEstimate");
      const counterOfferPayload = {
        id: selectedEstimate.id,
        discount_value: selectedEstimate?.discountValue,
        discount_type: selectedEstimate?.discountType,
        total_tax: totalTaxAmt,
        negotiationMessage: negotiationData.message,
        status: "negotiate",
        markup_value: 0,
        line_items: validProducts.map((p) => ({
          productId: p.productId,
          item_name: p.productName,
          qty: p.quantity,
          rate: p.unitPrice,
          description: p.description,
        })),
      };
      const response = await ApiClient.put(
        "generate-quote/update",
        counterOfferPayload,
      );

      if (response?.success) {
        setIsNegotiationModalOpen(false);
        setNegotiationData({ products: [], message: "", selectedProducts: [] });
        swal({
          icon: "success",
          title: "Counter Offer",
          text: "Counter offer sent successfully and moved to the Negotiation tab. Click below to view the details.",
          buttons: {
            cancel: {
              text: "Go Back",
              value: "back",
              visible: true,
            },
            confirm: {
              text: "View Details",
              value: "view",
            },
          },
        }).then((action) => {
          if (action === "view") {
            history(
              `/estimate/detail/${response?.data?._id}?date=${Date.now()}`,
            );
          } else if (action === "back") {
            history("/orders?tab=estimate");
          }
        });
      } else {
        toast.error(response?.message || "Failed to send counter offer");
      }
    } catch (error) {
      console.error("Error sending counter offer:", error);
      toast.error("Error sending counter offer. Please try again.");
    } finally {
      loader(false);
      setActionLoading(null);
    }
  };

  // ── Accept / Reject quote ─────────────────────────────────────────────────
  const updateQuoteStatus = async (quoteId, status, quoteData = {}, type) => {
    swal({
      title: `Are you sure you want to ${status} this quote?`,
      text: "This action cannot be undone.",
      icon: "warning",
      buttons: ["Cancel", "Yes"],
      dangerMode: status === "reject",
    }).then(async (willConfirm) => {
      if (willConfirm) {
        loader(true);
        setActionLoading(quoteId);
        const payload = { id: quoteId, status, po_number: quoteData?.poNumber };
        let url = "request-quote/customer/status/change";
        if (type == "quickbookEstimate") {
          url = "quickbook/update/estimate/status";
        }
        try {
          const response = await ApiClient.get(url, payload);
          if (response?.success) {
            setIsPoNumberOpen(false);
            const isAccepting = status === "accept";
            swal({
              icon: "success",
              title: isAccepting ? "Estimate Accepted!" : "Estimate Rejected!",
              text: isAccepting
                ? "The estimate has been accepted successfully and moved to the Accepted tab.\n Click below to view the details."
                : "The estimate has been rejected successfully and moved to the Rejected tab.\n Click below to view the details.",
              buttons: {
                cancel: {
                  text: "Go Back",
                  value: "back",
                  visible: true,
                },
                confirm: {
                  text: "View Details",
                  value: "view",
                },
              },
            }).then((action) => {
              const estimateId = response?.data?._id || quoteId;

              if (action === "view") {
                history(`/estimate/detail/${estimateId}?date=${Date.now()}`);
              } else if (action === "back") {
                history("/orders?tab=estimate");
              }
            });
          } else {
            toast.error(response?.message || `Failed to ${status} the quote`);
          }
        } catch (error) {
          console.error(`Error ${status}ing quote:`, error);
          toast.error(`Error ${status}ing quote. Please try again.`);
        } finally {
          loader(false);
          setActionLoading(null);
        }
      }
    });
  };

  return (
    <>
      <Header />
      <div className="min-h-[calc(100vh_-_75px)] md:min-h-[calc(100vh_-_95px)] mt-[75px] lg:mt-[95px]">
        <div className="container mx-auto px-5 py-8 sm:py-14">
          {/* Back + heading */}
          <div className="flex gap-3 flex-wrap items-center justify-between mb-8">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => history("/orders?tab=estimate")}
                className="size-9 rounded-xl border border-gray-200 hover:bg-gray-100 flex items-center justify-center text-gray-600 hover:text-gray-700 transition-colors"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="m15 18-6-6 6-6" />
                </svg>
              </button>
              <div>
                <h2 className="text-gray-900 text-xl sm:text-3xl font-semibold">
                  {selectedEstimate?.isPending
                    ? "Quote Details"
                    : "Estimate Details"}
                </h2>
                {/* <p className="text-gray-600 text-sm">
                View and manage your estimate
              </p> */}

                <p className="text-gray-600 text-sm">
                  #{selectedEstimate?.displayDocNumber}
                </p>
              </div>
            </div>
            <div className="flex flex-wrap items-center justify-end gap-2">
              {selectedEstimate?.status === "PENDING" && (
                <>
                  <button
                    type="button"
                    onClick={() => {
                      setIsPoNumberOpen(true);
                      setPoNumber("");
                    }}
                    disabled={actionLoading === selectedEstimate.id}
                    className={`w-fit inline-flex items-center gap-2 text-white text-xs sm:text-sm px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg font-medium transition-all ${
                      actionLoading === selectedEstimate.id
                        ? "opacity-50 cursor-not-allowed bg-[#2E9E34]"
                        : "bg-[#2E9E34] hover:bg-[#27872d]"
                    }`}
                  >
                    {actionLoading === selectedEstimate.id
                      ? "Processing..."
                      : "Accept Quote"}
                  </button>

                  <button
                    type="button"
                    onClick={() =>
                      updateQuoteStatus(
                        selectedEstimate.mainRequestId,
                        "reject",
                        selectedEstimate,
                        selectedEstimate.type,
                      )
                    }
                    disabled={actionLoading === selectedEstimate.id}
                    className={`w-fit inline-flex items-center gap-2 text-white text-xs sm:text-sm px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg font-medium transition-all ${
                      actionLoading === selectedEstimate.id
                        ? "opacity-50 cursor-not-allowed bg-[#E53935]"
                        : "bg-[#E53935] hover:bg-[#d32f2f]"
                    }`}
                  >
                    Reject Quote
                  </button>

                  {!selectedEstimate.isQB && (
                    <button
                      type="button"
                      onClick={openNegotiationModal}
                      disabled={actionLoading === selectedEstimate.id}
                      className={`w-fit inline-flex items-center gap-2 text-white text-xs sm:text-sm px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg font-medium transition-all ${
                        actionLoading === selectedEstimate.id
                          ? "opacity-50 cursor-not-allowed bg-[#1E63A9]"
                          : "bg-[#1E63A9] hover:bg-[#18558f]"
                      }`}
                    >
                      {actionLoading === selectedEstimate.id
                        ? "Processing..."
                        : "Make Changes"}
                    </button>
                  )}
                </>
              )}
            </div>
          </div>

          <div className="">
            {pageLoading ? (
              <></>
            ) : // <div className="flex items-center justify-center py-20">
            //   <div className="size-8 border-2 border-[#115B9D] border-t-transparent rounded-full animate-spin" />
            // </div>
            !selectedEstimate ? (
              <div className="flex items-center justify-center py-20">
                <p className="text-gray-600 text-sm">Estimate not found.</p>
              </div>
            ) : (
              <>
                {/* Content */}
                <div className="space-y-6">
                  <div className="flex items-center gap-4 mb-8">
                    <div className="size-16 sm:size-20 rounded-full bg-[#115B9D17] border border-[#115B9D]/15 flex items-center justify-center shrink-0">
                      <span className="text-xl font-bold text-[#115B9D] capitalize">
                        {getInitials(selectedEstimate.customerName || "")}
                      </span>
                    </div>
                    <div>
                      <p className="text-base font-semibold text-gray-900 capitalize">
                        {selectedEstimate.customerName || "—"}
                      </p>
                      <p className="text-sm text-gray-600 mt-0.5">
                        {selectedEstimate.email || "No email"}
                      </p>
                      <StatusBadge status={selectedEstimate.status} />
                    </div>
                  </div>

                  {/* Negotiation block */}
                  {(selectedEstimate.negotiateMessage ||
                    (selectedEstimate.requestedProducts &&
                      selectedEstimate.requestedProducts.length > 0)) && (
                    <div className="space-y-3 p-4 bg-blue-50 rounded-xl border border-blue-200">
                      <h3 className="text-sm font-semibold text-blue-800">
                        Customer Negotiation Request
                      </h3>
                      {selectedEstimate.negotiateMessage && (
                        <div>
                          <p className="text-xs text-blue-600 font-medium mb-1">
                            Message:
                          </p>
                          <p className="text-sm text-gray-600">
                            {selectedEstimate.negotiateMessage}
                          </p>
                        </div>
                      )}
                      {selectedEstimate.requestedProducts?.length > 0 && (
                        <div>
                          <p className="text-xs text-blue-600 font-medium mb-1">
                            Requested Products:
                          </p>
                          <div className="space-y-2">
                            {selectedEstimate.requestedProducts.map(
                              (product, idx) => (
                                <div
                                  key={idx}
                                  className="text-sm text-gray-600 bg-white rounded-lg p-2"
                                >
                                  <p className="font-medium">
                                    {product.productName ||
                                      product.itemName ||
                                      product.name}
                                  </p>
                                  <p className="text-xs text-gray-600">
                                    Qty: {product.quantity} | Unit Price:{" "}
                                    {formatCurrency(
                                      product.unitPrice ||
                                        product.rate ||
                                        product.amount,
                                    )}
                                  </p>
                                  {product.description && (
                                    <p className="text-xs text-gray-600 mt-1">
                                      {product.description}
                                    </p>
                                  )}
                                </div>
                              ),
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="flex max-lg:flex-wrap md:gap-x-6 gap-y-6">
                    {/* Quote Details */}
                    <div className="w-full space-y-6">
                      {/* Addresses */}
                      <div className="rounded-xl border">
                        <h2 className="col-span-12 flex items-center gap-2 border-b px-4 py-2">
                          <CiLocationOn />
                          Addresses
                        </h2>
                        <div className=" col-span-12 lg:col-span-6 xl:col-span-5 grid grid-cols-1 sm:grid-cols-2">
                          <div className="">
                            <div className="p-4">
                              <p className="text-sm font-semibold uppercase tracking-widest mb-1">
                                Billing Address
                              </p>
                              <p className="text-sm text-gray-600">
                                {selectedEstimate.billAddr?.Line1 || "—"}
                              </p>
                              {selectedEstimate.billAddr?.Line2 && (
                                <p className="text-sm text-gray-600">
                                  {selectedEstimate.billAddr?.Line2}
                                </p>
                              )}
                              <p className="text-sm text-gray-600">
                                {selectedEstimate.billAddr?.City}
                                {selectedEstimate.billAddr?.City
                                  ? ","
                                  : ""}{" "}
                                {selectedEstimate.billAddr?.Country}{" "}
                                {selectedEstimate.billAddr?.PostalCode}
                              </p>
                            </div>
                          </div>
                          <div className="">
                            <div className="p-4">
                              <p className="text-sm font-semibold uppercase tracking-widest mb-1">
                                Shipping Address
                              </p>
                              <p className="text-sm text-gray-600">
                                {selectedEstimate.shipAddr?.Line1 || "—"}
                              </p>
                              {selectedEstimate.shipAddr?.Line2 && (
                                <p className="text-sm text-gray-600">
                                  {selectedEstimate.shipAddr?.Line2}
                                </p>
                              )}
                              <p className="text-sm text-gray-600">
                                {selectedEstimate.shipAddr?.City}
                                {selectedEstimate.shipAddr?.City
                                  ? ","
                                  : ""}{" "}
                                {selectedEstimate.shipAddr?.Country}{" "}
                                {selectedEstimate.shipAddr?.PostalCode}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Line Items */}
                      <div className="rounded-xl border pb-2">
                        <h2 className="col-span-12 flex items-center gap-2 border-b px-4 py-2">
                          <GoPackage />
                          Line Items
                        </h2>
                        <div className="">
                          {/* Mobile View */}
                          <div className="sm:!hidden divide-y space-y-3">
                            {!selectedEstimate.lineItems ||
                            selectedEstimate.lineItems.length === 0 ? (
                              <div className="py-8 text-center text-gray-600">
                                No line items available
                              </div>
                            ) : (
                              selectedEstimate.lineItems.map((line, i) => (
                                <div key={i} className="bg-white p-4">
                                  <div className="mb-3">
                                    <p className="text-sm font-semibold text-[#0F172A]">
                                      {line.itemName}
                                    </p>
                                    {line.description && (
                                      <p className="text-xs text-gray-500 mt-1">
                                        {line.description}
                                      </p>
                                    )}
                                  </div>

                                  <div className="">
                                    <div className="grid grid-cols-2 gap-y-2 text-sm border-b border-gray-50 last:border-0 py-2">
                                      <span className="text-gray-500">Qty</span>
                                      <span className="text-right font-medium">
                                        {line.quantity}
                                      </span>
                                    </div>

                                    <div className="grid grid-cols-2 gap-y-2 text-sm border-b border-gray-50 last:border-0 py-2">
                                      <span className="text-gray-500">
                                        Unit Price
                                      </span>
                                      <span className="text-right font-medium">
                                        {formatCurrency(
                                          line.unitPrice,
                                          selectedEstimate.currencyCode,
                                        )}
                                      </span>
                                    </div>

                                    <div className="grid grid-cols-2 gap-y-2 text-sm border-b border-gray-50 last:border-0 py-2">
                                      <span className="text-gray-500">
                                        Amount
                                      </span>
                                      <span className="text-right font-semibold text-[#115B9D]">
                                        {formatCurrency(
                                          line.amount,
                                          selectedEstimate.currencyCode,
                                        )}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              ))
                            )}
                          </div>

                          {/* Desktop View */}
                          <div className="max-sm:!hidden overflow-auto">
                            <table className="w-full min-w-[480px]">
                              <thead className="bg-gray-50 border-b border-gray-100">
                                <tr>
                                  <th className="text-left py-2.5 px-4 text-sm font-medium text-gray-600">
                                    Item
                                  </th>
                                  <th className="text-center py-2.5 px-4 text-sm font-medium text-gray-600">
                                    Qty
                                  </th>
                                  <th className="text-right py-2.5 px-4 text-sm font-medium text-gray-600">
                                    Unit Price
                                  </th>
                                  <th className="text-right py-2.5 px-4 text-sm font-medium text-gray-600">
                                    Amount
                                  </th>
                                </tr>
                              </thead>

                              <tbody>
                                {!selectedEstimate.lineItems ||
                                selectedEstimate.lineItems.length === 0 ? (
                                  <tr>
                                    <td
                                      colSpan={4}
                                      className="py-8 text-center text-gray-600"
                                    >
                                      No line items available
                                    </td>
                                  </tr>
                                ) : (
                                  selectedEstimate.lineItems.map((line, i) => (
                                    <tr key={i} className="border-t">
                                      <td className="py-2.5 px-4">
                                        <p className="text-md text-[#0F172A]">
                                          {line.itemName}
                                        </p>
                                        {line.description && (
                                          <p className="text-sm text-gray-600 mt-0.5">
                                            {line.description}
                                          </p>
                                        )}
                                      </td>

                                      <td className="py-2.5 px-4 text-center">
                                        {line.quantity}
                                      </td>

                                      <td className="py-2.5 px-4 text-right">
                                        {formatCurrency(
                                          line.unitPrice,
                                          selectedEstimate.currencyCode,
                                        )}
                                      </td>

                                      <td className="py-2.5 px-4 text-right font-medium">
                                        {formatCurrency(
                                          line.amount,
                                          selectedEstimate.currencyCode,
                                        )}
                                      </td>
                                    </tr>
                                  ))
                                )}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="w-full lg:w-[400px] lg:min-w-[400px] space-y-6">
                      <div className="rounded-xl border pb-2">
                        <h2 className="col-span-12 flex items-center gap-2 border-b px-4 py-2">
                          <GoHash />
                          {selectedEstimate?.isPending
                            ? "Quote Details"
                            : "Estimate Details"}
                        </h2>
                        <div className="p-4 space-y-1">
                          <div className="pt-2 grid grid-cols-1 sm:grid-cols-2 gap-4">
                            {[
                              {
                                label: selectedEstimate?.isPending
                                  ? "Quote Number"
                                  : "Doc Number",
                                value: `#${selectedEstimate.displayDocNumber}`,
                              },
                              {
                                label: "PO Number",
                                value: selectedEstimate.poNumber,
                              },
                              {
                                label: "Date",
                                value: formatDate(selectedEstimate.txnDate),
                              },
                              ...(selectedEstimate.expirationDate
                                ? [
                                    {
                                      label: "Expiration Date",
                                      value: formatDate(
                                        selectedEstimate.expirationDate,
                                      ),
                                    },
                                  ]
                                : []),
                              ...(!selectedEstimate?.isPending
                                ? [
                                    {
                                      label: "Domain",
                                      value: selectedEstimate.domain || "—",
                                    },
                                  ]
                                : []),
                              {
                                label: "Status",
                                value: selectedEstimate.status || "—",
                              },
                            ]
                              .filter(Boolean)
                              .map((d) => (
                                <div key={d.label} className="">
                                  <p className="text-xs text-gray-600 font-medium uppercase tracking-wide">
                                    {d.label}
                                  </p>
                                  <p className="text-sm text-gray-800 font-medium mt-0.5 capitalize">
                                    {d.value}
                                  </p>
                                </div>
                              ))}
                          </div>
                        </div>
                      </div>
                      {/* Tax Detail */}
                      {/* {selectedEstimate.taxDetail && (
                          <div className="rounded-xl border pb-2">
                            <h2 className="col-span-12 flex items-center gap-2 border-b px-4 py-2">
                              <LuListTodo />
                              Tax Detail</h2>
                            <div className="p-4 space-y-1">
                              <div className="overflow-hidden">
                                {selectedEstimate.taxDetail.TaxLine?.length > 0 ? (
                                  selectedEstimate.taxDetail.TaxLine.map((tl, i) => (
                                    <div
                                      key={i}
                                      className="flex gap-x-3 gap-y-2 items-center justify-between border-b border-gray-50 last:border-0 py-2"
                                    >
                                      <p className="text-sm text-gray-600">
                                        Tax ({tl.TaxLineDetail?.TaxPercent ?? 0}%) on{" "}
                                        {formatCurrency(
                                          tl.TaxLineDetail?.NetAmountTaxable,
                                          selectedEstimate.currencyCode,
                                        )}
                                      </p>
                                      <p className="text-sm font-semibold text-gray-800">
                                        {formatCurrency(
                                          tl.Amount,
                                          selectedEstimate.currencyCode,
                                        )}
                                      </p>
                                    </div>
                                  ))
                                ) : (
                                  <div className="flex gap-x-3 gap-y-2 items-center justify-between  border-b border-gray-50 last:border-0 py-2">
                                    <p className="text-sm text-gray-600">Total Tax</p>
                                    <p className="text-sm font-semibold text-gray-800">
                                      {formatCurrency(
                                        selectedEstimate.taxDetail?.TotalTax ||
                                        selectedEstimate.tax,
                                        selectedEstimate.currencyCode,
                                      )}
                                    </p>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        )} */}

                      {/* Summary */}
                      <div className="rounded-xl border border-[#115B9D]/20 bg-gradient-to-br from-[#F8FBFF] to-white shadow-md pb-2">
                        <h2 className="col-span-12 flex items-center gap-2 border-b bg-[#115B9D]/5 px-4 py-3 text-[#115B9D] font-semibold">
                          <LuClipboardList />
                          Summary
                        </h2>

                        <div className="p-4 space-y-1">
                          <div className="overflow-hidden">
                            {[
                              {
                                label: "Subtotal",
                                value: formatCurrency(
                                  selectedEstimate.subtotal,
                                  selectedEstimate.currencyCode,
                                ),
                                highlight: false,
                              },
                              {
                                label: "Total Tax",
                                value: formatCurrency(
                                  selectedEstimate.tax,
                                  selectedEstimate.currencyCode,
                                ),
                                highlight: false,
                              },
                              {
                                label:
                                  selectedEstimate.discountType === "percent"
                                    ? `Discount (${selectedEstimate.discountValue}%)`
                                    : "Discount",
                                value: `- ${formatCurrency(
                                  selectedEstimate.discountAmt,
                                  selectedEstimate.currencyCode,
                                )}`,
                                highlight: false,
                                hide: !selectedEstimate.discountAmt,
                              },
                              {
                                label: "Total",
                                value: formatCurrency(
                                  selectedEstimate.total,
                                  selectedEstimate.currencyCode,
                                ),
                                highlight: true,
                              },
                            ]
                              .filter((row) => !row.hide)
                              .map((row) => {
                                const isDiscount =
                                  row.label.startsWith("Discount");

                                return (
                                  <div
                                    key={row.label}
                                    className={`flex items-center justify-between gap-x-3 gap-y-2 py-2 ${
                                      row.highlight
                                        ? "border-t-2 border-[#115B9D]/30 pt-4 mt-2 bg-[#115B9D]/5 rounded-lg px-3"
                                        : isDiscount
                                          ? "" // No border under Discount row
                                          : "border-b border-gray-100"
                                    }`}
                                  >
                                    <p
                                      className={`text-sm font-medium ${
                                        row.highlight
                                          ? "text-[#115B9D] text-base"
                                          : isDiscount
                                            ? "text-green-600"
                                            : "text-gray-600"
                                      }`}
                                    >
                                      {row.label}
                                    </p>

                                    <p
                                      className={`font-semibold ${
                                        row.highlight
                                          ? "text-[#115B9D] text-xl"
                                          : isDiscount
                                            ? "text-green-600 text-sm"
                                            : "text-gray-800 text-sm"
                                      }`}
                                    >
                                      {row.value}
                                    </p>
                                  </div>
                                );
                              })}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* ─── PO Number Modal ─── */}
          <Transition appear show={isPoNumberOpen} as={Fragment}>
            <Dialog
              as="div"
              className="relative z-[999]"
              onClose={() => setIsPoNumberOpen(false)}
            >
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-200"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="ease-in duration-150"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <div className="fixed inset-0 bg-black/30 backdrop-blur-sm" />
              </Transition.Child>
              <div className="fixed inset-0 overflow-y-auto">
                <div className="flex min-h-full items-center justify-center p-4">
                  <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-200"
                    enterFrom="opacity-0 scale-95"
                    enterTo="opacity-100 scale-100"
                    leave="ease-in duration-150"
                    leaveFrom="opacity-100 scale-100"
                    leaveTo="opacity-0 scale-95"
                  >
                    <Dialog.Panel className="w-full max-w-2xl bg-white rounded-2xl shadow-xl flex flex-col max-h-[90vh]">
                      <div className="flex items-center justify-between px-6 py-5 border-b shrink-0">
                        <div className="flex items-center gap-3">
                          <div className="size-9 rounded-xl bg-[#115B9D17] flex items-center justify-center">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="17"
                              height="17"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              className="text-[#115B9D]"
                            >
                              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                              <polyline points="14 2 14 8 20 8" />
                              <line x1="16" y1="13" x2="8" y2="13" />
                            </svg>
                          </div>
                          <div>
                            <Dialog.Title className="text-base font-semibold text-gray-900" />
                            {selectedEstimate && (
                              <div className="mt-1">
                                <StatusBadge status={selectedEstimate.status} />
                              </div>
                            )}
                          </div>
                        </div>
                        <button
                          onClick={() => setIsPoNumberOpen(false)}
                          className="size-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-600 hover:text-gray-600 transition-colors"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="18"
                            height="18"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M18 6 6 18M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                      <div className="p-5">
                        <label className="text-xs text-gray-600 font-medium uppercase tracking-wide">
                          Enter PO Number{" "}
                          <span className="text-[9px]">
                            ( Skip if not applicable )
                          </span>
                        </label>
                        <input
                          type="text"
                          value={poNumber}
                          onChange={(e) => setPoNumber(e.target.value)}
                          placeholder="PO Number"
                          className="w-full mt-2 px-4 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#115B9D]/20"
                        />
                      </div>
                      <div className="flex items-center justify-end gap-3 px-6 py-4 border-t shrink-0">
                        <button
                          type="button"
                          onClick={() =>
                            updateQuoteStatus(
                              selectedEstimate.mainRequestId,
                              "accept",
                              { poNumber },
                              selectedEstimate?.type,
                            )
                          }
                          className="w-fit inline-flex items-center gap-2 bg-[#115B9D] hover:opacity-80 text-white text-sm px-4 py-2.5 rounded-xl transition-colors font-medium"
                        >
                          Accept
                        </button>
                        <button
                          type="button"
                          onClick={() => setIsPoNumberOpen(false)}
                          className="px-5 py-2.5 text-sm text-gray-600 hover:opacity-80 rounded-xl transition-colors border border-gray-200 bg-white"
                        >
                          Close
                        </button>
                      </div>
                    </Dialog.Panel>
                  </Transition.Child>
                </div>
              </div>
            </Dialog>
          </Transition>

          {/* ─── Negotiation Modal ─── */}
          <Transition appear show={isNegotiationModalOpen} as={Fragment}>
            <Dialog
              as="div"
              className="relative z-[999]"
              onClose={() => setIsNegotiationModalOpen(false)}
            >
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-200"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="ease-in duration-150"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <div className="fixed inset-0 bg-black/30 backdrop-blur-sm" />
              </Transition.Child>
              <div className="fixed inset-0 overflow-y-auto">
                <div className="flex min-h-full items-center justify-center p-4">
                  <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-200"
                    enterFrom="opacity-0 scale-95"
                    enterTo="opacity-100 scale-100"
                    leave="ease-in duration-150"
                    leaveFrom="opacity-100 scale-100"
                    leaveTo="opacity-0 scale-95"
                  >
                    <Dialog.Panel className="w-full max-w-3xl bg-white rounded-2xl shadow-xl flex flex-col max-h-[90vh]">
                      <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100 shrink-0">
                        <div className="flex items-center gap-3">
                          <div className="size-9 rounded-xl bg-blue-50 flex items-center justify-center">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="17"
                              height="17"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              className="text-blue-600"
                            >
                              <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
                              <path d="M12 22V12" />
                              <path d="M9 10.5l3-3 3 3" />
                            </svg>
                          </div>
                          <div>
                            <Dialog.Title className="text-base font-semibold text-gray-900">
                              Send Counter Offer
                            </Dialog.Title>
                            <p className="text-xs text-gray-600 mt-0.5">
                              {selectedEstimate?.customerName}
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() => setIsNegotiationModalOpen(false)}
                          className="size-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-600 hover:text-gray-600 transition-colors"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="18"
                            height="18"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M18 6 6 18M6 6l12 12" />
                          </svg>
                        </button>
                      </div>

                      <div className="px-6 py-5 overflow-y-auto flex-1 space-y-5">
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Negotiation Message{" "}
                            <span className="text-red-500">*</span>
                          </label>
                          <textarea
                            rows="4"
                            className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all"
                            placeholder="Write your counter offer message here..."
                            value={negotiationData.message}
                            onChange={(e) =>
                              setNegotiationData((prev) => ({
                                ...prev,
                                message: e.target.value,
                              }))
                            }
                          />
                        </div>

                        <div>
                          <div className="flex items-center justify-between mb-3">
                            <label className="text-sm font-semibold text-gray-700">
                              Proposed Products{" "}
                              <span className="text-red-500">*</span>
                            </label>
                            <button
                              type="button"
                              onClick={addNegotiationProduct}
                              className="text-xs text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="14"
                                height="14"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <circle cx="12" cy="12" r="10" />
                                <line x1="12" y1="8" x2="12" y2="16" />
                                <line x1="8" y1="12" x2="16" y2="12" />
                              </svg>
                              Add Product
                            </button>
                          </div>
                          <div className="space-y-3">
                            {negotiationData.selectedProducts.length === 0 && (
                              <div className="text-center py-8 bg-gray-50 rounded-xl border border-gray-200">
                                <p className="text-sm text-gray-600">
                                  No products added yet. Click "Add Product" to
                                  propose products.
                                </p>
                              </div>
                            )}
                            {negotiationData.selectedProducts.map(
                              (product, index) => (
                                <div
                                  key={product.id}
                                  className="border border-gray-200 rounded-xl p-4 bg-gray-50/30"
                                >
                                  <div className="flex justify-end mb-2">
                                    <button
                                      type="button"
                                      onClick={() =>
                                        removeNegotiationProduct(index)
                                      }
                                      className="text-red-400 hover:text-red-600"
                                    >
                                      <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="16"
                                        height="16"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      >
                                        <line x1="18" y1="6" x2="6" y2="18" />
                                        <line x1="6" y1="6" x2="18" y2="18" />
                                      </svg>
                                    </button>
                                  </div>
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                      <label className="block text-xs font-medium text-gray-600 mb-1">
                                        Product
                                      </label>
                                      <Select
                                        menuPortalTarget={document.body}
                                        styles={{
                                          menuPortal: (base) => ({
                                            ...base,
                                            zIndex: 9999,
                                          }),
                                        }}
                                        value={
                                          availableProducts
                                            .map((p) => ({
                                              value: p.Id,
                                              label: p.Name,
                                            }))
                                            .find(
                                              (option) =>
                                                option.value ===
                                                product.productId,
                                            ) || null
                                        }
                                        onChange={(selected) =>
                                          updateNegotiationProduct(
                                            index,
                                            "productId",
                                            selected?.value || "",
                                          )
                                        }
                                        options={availableProducts.map((p) => ({
                                          value: p.Id,
                                          label: p.Name,
                                        }))}
                                        placeholder="Select Product"
                                        // isClearable
                                        className="text-sm"
                                      />
                                    </div>
                                    <div>
                                      <label className="block text-xs font-medium text-gray-600 mb-1">
                                        Quantity
                                      </label>
                                      <input
                                        type="number"
                                        min="1"
                                        onWheel={(e) => e.target.blur()}
                                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                                        value={product.quantity}
                                        onChange={(e) =>
                                          updateNegotiationProduct(
                                            index,
                                            "quantity",
                                            e.target.value,
                                          )
                                        }
                                      />
                                    </div>
                                    <div>
                                      <label className="block text-xs font-medium text-gray-600 mb-1">
                                        Unit Price ($)
                                      </label>
                                      <input
                                        type="number"
                                        min="0"
                                        onWheel={(e) => e.target.blur()}
                                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                                        value={product.unitPrice}
                                        onChange={(e) =>
                                          updateNegotiationProduct(
                                            index,
                                            "unitPrice",
                                            e.target.value,
                                          )
                                        }
                                      />
                                    </div>
                                    <div>
                                      <label className="block text-xs font-medium text-gray-600 mb-1">
                                        Description
                                      </label>
                                      <input
                                        type="text"
                                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                                        value={product.description}
                                        onChange={(e) =>
                                          updateNegotiationProduct(
                                            index,
                                            "description",
                                            e.target.value,
                                          )
                                        }
                                        placeholder="Optional description"
                                      />
                                    </div>
                                  </div>
                                </div>
                              ),
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="flex flex-wrap items-center justify-end gap-2 px-6 py-4 border-t border-gray-100 shrink-0">
                        <button
                          type="button"
                          onClick={() => setIsNegotiationModalOpen(false)}
                          className="px-5 py-2.5 text-sm text-gray-600 hover:opacity-80 rounded-xl transition-colors border border-gray-200 bg-white"
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={sendCounterOffer}
                          disabled={actionLoading === selectedEstimate?.id}
                          className={`w-fit inline-flex items-center gap-2 bg-[#115B9D] hover:opacity-80 text-white text-sm px-4 py-2.5 rounded-xl transition-colors font-medium ${actionLoading === selectedEstimate?.id ? "opacity-50 cursor-not-allowed" : ""}`}
                        >
                          {actionLoading === selectedEstimate?.id
                            ? "Sending..."
                            : "Send Counter Offer"}
                        </button>
                      </div>
                    </Dialog.Panel>
                  </Transition.Child>
                </div>
              </div>
            </Dialog>
          </Transition>
        </div>
      </div>
      {methodModel.TokenChecker() && <Footer />}
    </>
  );
}
